import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { type CardGroup, getTypeSortOrder, groupCards } from '@/lib/cardGrouping';
import { getCmc } from '@/lib/cardUtils';

import { type DeckGroupBy, deckGroupByAtom } from '../state/deckEditorSettings';
import { EnrichedDeckCard } from '../types';

export type { CardGroup } from '@/lib/cardGrouping';

/**
 * Groups deck cards for the list/grid/stack views.
 * Pass `groupByOverride` to ignore the persisted deck-wide preference.
 */
export default function useCardGroups(cards: EnrichedDeckCard[], groupByOverride?: DeckGroupBy): CardGroup<EnrichedDeckCard>[] {
  const persistedGroupBy = useAtomValue(deckGroupByAtom);
  const groupBy = groupByOverride ?? persistedGroupBy;

  return useMemo(
    () =>
      groupCards(
        cards,
        groupBy,
        (deckCard) => ({
          typeCategory: deckCard.classification.type_category,
          cmc: getCmc(deckCard.card) ?? 0,
          colorIdentity: deckCard.card.color_identity,
          quantity: deckCard.quantity,
        }),
        (a, b) => {
          if (groupBy === 'cmc') {
            // Within CMC groups, sort by type then name
            const typeDiff = getTypeSortOrder(a.classification.type_category) - getTypeSortOrder(b.classification.type_category);
            if (typeDiff !== 0) return typeDiff;
          } else {
            // Within type/color groups, sort by CMC then name
            const cmcDiff = (getCmc(a.card) ?? 0) - (getCmc(b.card) ?? 0);
            if (cmcDiff !== 0) return cmcDiff;
          }
          return a.card.name.localeCompare(b.card.name);
        },
      ),
    [cards, groupBy],
  );
}
